import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "shared/BaseFragment";
import React, {ReactElement} from "react";
import {PageWithSidebarContainer} from "shared/PageWithSidebarContainer";
import {SidebarItem, SidebarTab} from "shared/Sidebar";
import {PathComponent, PathProps} from "index";
import {Action, ListItemChange, OnListItemsListener} from "shared/types";
import {ChatFragment} from "./chat/ChatFragment";
import {ChatMessages, ChatThread, ChatThreads, ParticipantKey} from "./chat/types";
import {AddOutlined} from "@mui/icons-material";
import {StyledEmpty} from "shared/StyledComponents";
import {DW_SM} from "shared/dimens";
import chatsImage from "./res/images/chats.svg";
import {NotFoundFragment} from "../../../shared/NotFoundFragment";
import {MessagingFeatures} from "./types";

export type MessagesFragmentProps = BaseFragmentProps & {
  disableNewChat?: boolean,
}

export type MessagesFragmentState = BaseFragmentState & {
  sidebarTabs?: SidebarTab[],
}

export abstract class MessagesFragment<P extends MessagesFragmentProps = MessagesFragmentProps, S extends MessagesFragmentState = MessagesFragmentState> extends BaseFragment<P, S> implements OnListItemsListener<ChatThread> {

  private _chatThreads: ChatThreads;

  constructor(props: P, context: any, readonly myParticipantKey: ParticipantKey, protected readonly features?: MessagingFeatures) {
    super(props, context);
  }

  protected isMyParticipantKeyValid(): boolean {
    return Boolean(this.myParticipantKey);
  }

  protected getChatThreads(): ChatThreads {
    if (!this.isMyParticipantKeyValid()) {
      return null;
    }
    if (!this._chatThreads) {
      this._chatThreads = ChatThreads.getInstance(this.myParticipantKey.type);
    }
    return this._chatThreads;
  }

  protected getChatMessages(threadId: string): ChatMessages {
    if (!this.isMyParticipantKeyValid()) {
      return null;
    }
    return ChatMessages.getInstance(this.myParticipantKey.type, threadId);
  }

  componentDidMount() {
    super.componentDidMount();
    if (!this.isMyParticipantKeyValid()) {
      return;
    }
    this.getChatThreads().registerObserver(this);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    if (!this.isMyParticipantKeyValid()) {
      return;
    }
    this.getChatThreads().unregisterObserver(this);
  }

  onItemChanged(item: ChatThread, change: ListItemChange) {
    this.forceUpdate();
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    if (!this.isMyParticipantKeyValid()) {
      return;
    }
    const chatThreads = (await this.getChatThreads().getOrLoadListItems())
      .filter(thread => Boolean(thread.participantKeys.find(value => this.myParticipantKey.equals(value))));
    // const emailThreads = await this.emailThreads.getOrLoadThreads();
    this.setState({
      sidebarTabs: [
        {
          id: "chats",
          title: "Chats",
          action: new Action("New chat", () => this.onNewChat(), AddOutlined).setVariant("contained"),
          items: {
            groups: [
              {
                items: [
                  {
                    path: "chats",
                    hidden: true,
                    render: () => <StyledEmpty
                      imageStyle={{
                        width: DW_SM,
                      }}
                      emptyConfig={{
                        title: "No chat selected",
                        text: "Select a chat from the panel on the left.",
                        iconUrl: chatsImage,
                      }}/>,
                  },
                  ...chatThreads.map(thread => {
                    return {
                      path: "chats/" + thread.id,
                      iconUrl: thread.getProfilePhoto(),
                      title: thread.getDisplayName(),
                      text: thread.lastMessage?.text,
                      // text: SnippetFactory.getInstance().getMessageSnippet(thread.lastMessage),
                      render: (pathProps: PathProps) => {
                        return <ChatFragment
                          features={this.features?.chat}
                          path={pathProps}
                          myParticipantKey={this.myParticipantKey}
                          thread={thread}/>;
                      }
                    } as SidebarItem;
                  }),
                ],
              },
            ],
            containerId: "messages",
          },
        },
        // {
        //   id: "emails",
        //   title: "Emails",
        //   action: new Action("New email", () => this.onNewEmail(), AddOutlined).setVariant("contained"),
        //   items: {
        //     groups: [
        //       {
        //         items: [
        //           {
        //             path: "emails/inbox",
        //             title: "Inbox",
        //             iconType: InboxOutlined,
        //             render: (pathProps: PathProps) => <EmailsInboxFragment path={pathProps}/>,
        //           },
        //           {
        //             path: "emails/sent",
        //             title: "Sent",
        //             iconType: SendOutlined,
        //             render: (pathProps: PathProps) => <EmailsSentFragment path={pathProps}/>,
        //           },
        //           {
        //             path: "emails/drafts",
        //             title: "Drafts",
        //             iconType: DraftsOutlined,
        //             render: (pathProps: PathProps) => <EmailsDraftsFragment path={pathProps}/>,
        //           },
        //           {
        //             path: "emails/trash",
        //             title: "Trash",
        //             iconType: DeleteOutlined,
        //             render: (pathProps: PathProps) => <EmailsTrashFragment path={pathProps}/>,
        //           },
        //         ],
        //         variant: "condensed",
        //       },
        //       {
        //         items: [
        //           {
        //             path: "emails",
        //             hidden: true,
        //             render: () => this.renderUnselected(),
        //           },
        //           // ...emailThreads.map(thread => {
        //           //   return {
        //           //     path: "emails/" + thread.key.id,
        //           //     iconUrl: UserProfilePhoto(thread.other),
        //           //     title: UserDisplayName(thread.other),
        //           //     render: (pathProps: PathProps) => {
        //           //       return <EmailFragment path={pathProps} other={thread.other}/>;
        //           //     }
        //           //   } as SidebarItem;
        //           // }),
        //         ],
        //       },
        //     ],
        //     containerId: "messages",
        //   },
        // },
      ],
    });
  }

  static nestedPaths(): PathComponent[] {
    return [
      {
        path: "chats",
      },
      {
        path: "chats/:thread_id",
        handle: {containerId: "messages", path: "chats/:thread_id"},
      },
      // {
      //   path: "emails/inbox",
      //   handle: {containerId: "messages", path: "emails/inbox"},
      // },
      // {
      //   path: "emails/sent",
      //   handle: {containerId: "messages", path: "emails/sent"},
      // },
      // {
      //   path: "emails/drafts",
      //   handle: {containerId: "messages", path: "emails/drafts"},
      // },
      // {
      //   path: "emails/trash",
      //   handle: {containerId: "messages", path: "emails/trash"},
      // },
      // {
      //   path: "emails",
      // },
      // {
      //   path: "emails/:thread_id",
      //   handle: {containerId: "messages", path: "emails/:thread_id"},
      // }
    ];
  }

  protected renderContainerContent(): ReactElement | null {
    if (!this.isMyParticipantKeyValid()) {
      return <NotFoundFragment path={this.props.path}/>;
    }
    return <PageWithSidebarContainer
      path={this.props.path}
      sidebarTabs={this.state.sidebarTabs}/>
  }

  protected onNewChat(): void {
  }

  protected onNewEmail(): void {
  }
}
